import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";
import type { ReactNode } from "react";
import { seo } from "@/lib/seo";

const POSTS: Record<
  string,
  { title: string; date: string; image?: string; body: ReactNode[]; seoTitle?: string; seoDesc?: string }
> = {
  "matcha-grades": {
    title: "The 4 Universal Commercial Grades of Matcha",
    date: "June 2026",
    image: "/assets/images/journal/grades.png",
    seoTitle: "Matcha Grades Explained | Bulk Ceremonial Matcha Supplier UAE",
    seoDesc: "Understand the differences between ceremonial and culinary matcha grades. QT Foods provides premium bulk matcha for hospitality and private label brands in the GCC.",
    body: [
      "While no standardized international government grading body exists, the global tea industry adheres strictly to a de facto four-tier hierarchy determined by harvest timing, shading duration, milling methodology, and chemical composition:",
      <div className="my-10 overflow-x-auto rounded-sm border border-border bg-card shadow-sm">
        <table className="w-full text-left text-sm text-foreground/80">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr>
              <th className="px-5 py-4 font-medium whitespace-nowrap">Grade</th>
              <th className="px-5 py-4 font-medium whitespace-nowrap">Harvest / Process</th>
              <th className="px-5 py-4 font-medium whitespace-nowrap">Visual Quality</th>
              <th className="px-5 py-4 font-medium whitespace-nowrap">Key Attribute</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                Ceremonial
              </td>
              <td className="px-5 py-4">First Flush Hand-Picked | 100% Stone-Milled</td>
              <td className="px-5 py-4">Max Chlorophyll & Amino Acids</td>
              <td className="px-5 py-4">Zero Bitterness</td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">Premium</td>
              <td className="px-5 py-4">First Flush Dominant</td>
              <td className="px-5 py-4">Vibrant Emerald</td>
              <td className="px-5 py-4">Dynamic Latte / Balanced Umami</td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">Cooking</td>
              <td className="px-5 py-4">Later Harvest Leaves</td>
              <td className="px-5 py-4">Robust Astringency</td>
              <td className="px-5 py-4">High-Heat Resilience / Cost-Effective</td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                Commercial
              </td>
              <td className="px-5 py-4">Industrial Master Blend | Machine-Milled</td>
              <td className="px-5 py-4">Standardized Food Service</td>
              <td className="px-5 py-4">Maximum Economy</td>
            </tr>
          </tbody>
        </table>
      </div>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">Grade I: Ceremonial Grade</h2>,
      <p>
        <strong>Target Application:</strong> Traditional tea ceremonies (Koicha thick tea / Usucha
        thin tea), formal hospitality, and boutique gifting.
      </p>,
      <p>
        <strong>Manufacturing Standards:</strong> Crafted exclusively from first flush spring buds
        grown under extended shade. Milled slowly via granite stone wheels.
      </p>,
      <p>
        <strong>Sensory Profile:</strong> Electric, luminous jade green. Exceptionally high amino
        acid concentration creates profound umami sweetness with virtually zero astringency.
      </p>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">
        Grade II: Premium Grade (Café Latte Grade)
      </h2>,
      <p>
        <strong>Target Application:</strong> High-end boutique café beverage formulations and daily
        personal consumption.
      </p>,
      <p>
        <strong>Manufacturing Standards:</strong> Utilizes elite first flush foliage harvested
        slightly later in the spring cycle.
      </p>,
      <p>
        <strong>Sensory Profile:</strong> Vibrant emerald green. Engineered with a dynamic
        structural balance of sweet umami and pleasant briskness designed to cut cleanly through
        dairy and oat milk bases without losing aromatic clarity.
      </p>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">
        Grade III: Cooking Grade (Culinary Confectionery)
      </h2>,
      <p>
        <strong>Target Application:</strong> High-heat commercial baking, pastry patisserie,
        chocolatiers, and craft gastronomy.
      </p>,
      <p>
        <strong>Manufacturing Standards:</strong> Curated from mature second flush and autumn tea
        foliage.
      </p>,
      <p>
        <strong>Sensory Profile:</strong> Deep olive-green. Contains elevated catechin levels that
        provide a sharp, punchy astringency. This robust structural bitterness is specifically
        designed to harmonize with heavy sugars, fats, and dairy creams while retaining bold green
        tea flavor under intense baking temperatures.
      </p>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">
        Grade IV: Commercial Grade (Food Processing & Industrial)
      </h2>,
      <p>
        <strong>Target Application:</strong> Large-scale manufacturing, ready-to-drink (RTD) bottled
        beverages, industrial ice cream production, and global chain food service.
      </p>,
      <p>
        <strong>Manufacturing Standards:</strong> Master-blended across multiple seasonal harvests
        and machine-milled for extreme output efficiency.
      </p>,
      <p>
        <strong>Sensory Profile:</strong> Standardized muted green. Prioritizes absolute chemical
        stability, color preservation against citric acids/light degradation, and aggressive
        cost-efficiency for mass-market commercial scaling.
      </p>,
    ],
  },
  "science-of-matcha": {
    title: "Masterclass: The Science & Standards of Matcha",
    date: "May 2026",
    image: "/assets/images/journal/matcha.png",
    seoTitle: "The Science of Authentic Matcha | Bulk Supplier GCC",
    seoDesc: "Masterclass on the science and standards of true Japanese matcha. Reliable wholesale supply for restaurants, cafes, and private labels in the UAE and Saudi Arabia.",
    body: [
      <p className="text-xl leading-relaxed text-foreground/90 italic border-l-4 border-primary/30 pl-5 my-8 text-muted-foreground font-serif">
        Matcha is strictly defined as: "A powdered green tea made by grinding dried tea leaves
        (Tencha) that were grown under shade and not rolled, using a stone mill until finely
        powdered".
      </p>,
      <div className="my-10 p-6 md:p-10 bg-cream border border-border rounded-sm overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/rice-paper-2.png')] opacity-30 mix-blend-multiply" />
        <div className="relative flex flex-wrap items-center justify-center gap-y-4 gap-x-3">
          {[
            "Fresh Raw Leaves",
            "Steamed & Dried",
            "Tencha Aracha",
            "Wind/Sieve Sorting (Demono)",
            "Shitate Tencha",
            "Stone-Mill Grinding",
            "Matcha Powder",
          ].map((step, i, arr) => (
            <div key={step} className="flex items-center gap-3">
              <div className="bg-background border border-border px-3 py-2 text-xs font-medium uppercase tracking-[0.1em] text-foreground shadow-sm">
                {step}
              </div>
              {i < arr.length - 1 && <div className="text-primary/50 text-lg">→</div>}
            </div>
          ))}
        </div>
      </div>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">The Tencha Refining Process</h2>,
      <p>
        Unlike Sencha, Tencha leaves are never rolled. After harvesting and steaming, the foliage is
        dried flat into Tencha Aracha. To eliminate harsh structural bitterness, these rough leaves
        pass through air-current sorters and mechanical sieves to strip away fibrous stems and rigid
        leaf veins (collectively termed <em>Demono</em>). The remaining pure, delicate leaf flesh is
        designated as <em>Shitate Tencha</em> (refined tencha). Grinding this refined flesh yields
        matcha.
      </p>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">
        Physical Properties & Fragility
      </h2>,
      <p>
        Matcha is milled to an ultra-fine particle size of 5 to 10 microns. Due to this massive
        surface-area-to-volume ratio, matcha is highly unstable:
      </p>,
      <ul className="list-disc pl-5 space-y-2">
        <li>It absorbs ambient moisture and external odors almost instantly.</li>
        <li>
          UV radiation and light exposure degrade internal chlorophyll, causing rapid color fading.
        </li>
      </ul>,
      <p>
        To prevent premature oxidation, elite distributors store unground Shitate Tencha in
        refrigerated, dehumidified vaults, executing stone-milling strictly on demand. Controlled
        cold-aging allows amino acids to mature, unlocking a sweet sensory bouquet known as{" "}
        <em>Ooika</em> (覆い香 or "over-the-top fragrance").
      </p>,
    ],
  },
  "regional-terroir": {
    title: "Regional Terroir: Characteristics by Prefecture",
    date: "April 2026",
    image: "/assets/images/journal/terroir.png",
    seoTitle: "Japanese Tea Terroir | Wholesale Importer Saudi Arabia & Dubai",
    seoDesc: "Explore the regional terroirs of Japan's finest tea-producing areas. Direct importer of Uji and Shizuoka bulk green tea for the Gulf region.",
    body: [
      "Japan's diverse geographic climate creates distinct regional tea identities:",
      <div className="my-10 grid gap-6 sm:grid-cols-2">
        <div className="bg-card border border-border p-6 rounded-sm shadow-sm flex flex-col hover:border-primary/30 transition-colors">
          <h3 className="font-serif text-xl text-foreground mb-1">Shizuoka Prefecture</h3>
          <span className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
            The Volume Giant
          </span>
          <p className="text-sm">
            Produces ≈40% of Japan's total domestic tea supply. Celebrated for exceptionally
            balanced, highly versatile Yabukita cultivar Sencha.{" "}
            <strong className="text-foreground">Highlight:</strong> The city of Kakegawa is globally
            recognized as the birthplace of deep-steamed Fukamushi tea.
          </p>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm shadow-sm flex flex-col hover:border-primary/30 transition-colors">
          <h3 className="font-serif text-xl text-foreground mb-1">Kagoshima Prefecture</h3>
          <span className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
            The Southern Innovator
          </span>
          <p className="text-sm">
            Japan's second-largest production region. Benefiting from a warm southern climate,
            Kagoshima executes early spring harvests and utilizes advanced multi-cultivar blending
            techniques (e.g., Chiran and Kirishima teas) to produce rich, sweet flavor profiles.
          </p>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm shadow-sm flex flex-col hover:border-primary/30 transition-colors">
          <h3 className="font-serif text-xl text-foreground mb-1">Kyoto Prefecture</h3>
          <span className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
            The Cultural Heartland
          </span>
          <p className="text-sm">
            Home to the legendary terroir of Uji (including Wazuka and Ujitawara). Renowned for
            producing the pinnacle of refined, highly aromatic Gyokuro and ceremonial Matcha.
          </p>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm shadow-sm flex flex-col hover:border-primary/30 transition-colors">
          <h3 className="font-serif text-xl text-foreground mb-1">Fukuoka Prefecture</h3>
          <span className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
            The Umami Masters
          </span>
          <p className="text-sm">
            Famous for Yame Tea. Yame City utilizes traditional straw-shading and meticulous
            hand-picking to produce Japan's most intensely sweet, competition-grade Gyokuro.
          </p>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm shadow-sm flex flex-col hover:border-primary/30 transition-colors">
          <h3 className="font-serif text-xl text-foreground mb-1">Saitama Prefecture</h3>
          <span className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
            The Northern Limit
          </span>
          <p className="text-sm">
            Renowned for Sayama Tea and its heavy, sweet traditional roasting technique.
            Immortalized in the ancient proverb: "Color is Shizuoka, aroma is Uji, and flavor is
            Sayama to top it off".
          </p>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm shadow-sm flex flex-col hover:border-primary/30 transition-colors">
          <h3 className="font-serif text-xl text-foreground mb-1">Aichi Prefecture</h3>
          <span className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
            The Matcha Boutique
          </span>
          <p className="text-sm">
            Celebrated for Nishio Tea, producing bright, accessible, smooth green teas and boutique
            matcha blends.
          </p>
        </div>
      </div>,
    ],
  },
  "biochemical-composition": {
    title: "Biochemical Composition & Health Benefits",
    date: "March 2026",
    image: "/assets/images/journal/health.png",
    seoTitle: "Health Benefits of Japanese Tea | Wholesale Supplier UAE & KSA",
    seoDesc: "Discover the biochemical properties of premium Japanese green tea. QT Foods is the leading B2B supplier of authentic bulk matcha & sencha for the GCC region.",
    body: [
      "Japanese green tea is a powerhouse of bio-active functional compounds.",
      <div className="my-10 p-6 md:p-8 bg-card border border-border rounded-sm shadow-sm">
        <h3 className="font-serif text-xl mb-6 text-foreground">Catechins (Polyphenols)</h3>
        <ul className="space-y-4 pl-6 border-l-2 border-primary/30 relative">
          <li className="relative">
            <span className="absolute -left-[26px] top-3 w-4 border-t-2 border-primary/30"></span>
            <div className="bg-muted px-4 py-3 border border-border text-sm font-medium rounded-sm inline-block">
              <span className="text-foreground">EGCG (Epigallocatechin Gallate)</span>
              <span className="text-muted-foreground ml-2 text-xs uppercase tracking-wider">
                ~50% [Primary Fat-Burner]
              </span>
            </div>
          </li>
          <li className="relative">
            <span className="absolute -left-[26px] top-3 w-4 border-t-2 border-primary/30"></span>
            <div className="bg-muted px-4 py-3 border border-border text-sm font-medium rounded-sm inline-block text-foreground">
              EGC (Epigallocatechin)
            </div>
          </li>
          <li className="relative">
            <span className="absolute -left-[26px] top-3 w-4 border-t-2 border-primary/30"></span>
            <div className="bg-muted px-4 py-3 border border-border text-sm font-medium rounded-sm inline-block text-foreground">
              EC (Epicatechin)
            </div>
          </li>
          <li className="relative">
            <span className="absolute -left-[26px] top-3 w-4 border-t-2 border-primary/30"></span>
            <div className="bg-muted px-4 py-3 border border-border text-sm font-medium rounded-sm inline-block text-foreground">
              ECG (Epicatechin Gallate)
            </div>
          </li>
        </ul>
      </div>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">Functional Compounds Overview</h2>,
      <p>
        <strong>Catechins (Antioxidant Polyphenols):</strong> Dominated by EGCG (≈50% of total
        catechins), alongside EGC, EC, and ECG. These powerful antioxidants combat cellular aging,
        suppress oral pathogens (bad breath/cavities), eradicate Helicobacter pylori, accelerate
        lipid (fat) oxidation, and regulate blood glucose and arterial cholesterol. Concentration is
        highest in Sencha and Gyokuro, but drops in Hojicha due to thermal degradation.
      </p>,
      <p>
        <strong>Caffeine:</strong> Stimulates central nervous system cognition, memory, and
        metabolic fat breakdown. When bound naturally to L-Theanine, it provides sustained, calm
        alertness without jittery crashes. Caffeine hierarchy: Gyokuro &gt; Matcha &gt; Sencha &gt;
        Bancha &gt; Genmaicha &gt; Hojicha.
      </p>,
      <p>
        <strong>L-Theanine (Amino Acid):</strong> Generates brain alpha waves to induce deep
        relaxation while maintaining focus. Directly dampens sympathetic stress responses and
        improves sleep architecture. Sunlight converts L-Theanine into catechins; therefore, heavily
        shaded teas (Gyokuro/Matcha) possess the highest concentration of this calming compound.
      </p>,
      <p>
        <strong>Essential Vitamins & Minerals:</strong> Packed with Vitamin C (skin
        health/immunity), Vitamin E (anti-aging/circulation), and Vitamin B complex. Contains
        Potassium (blood pressure stabilization), Magnesium (neuromuscular health), and natural
        Fluoride (enamel fortification). To protect heat-sensitive Vitamin C, steep loose-leaf green
        teas at 70°C–80°C.
      </p>,
    ],
  },
  "traditional-tea-typologies": {
    title: "The 12 Traditional Japanese Tea Typologies",
    date: "February 2026",
    image: "/assets/images/journal/typologies.png",
    seoTitle: "12 Types of Japanese Tea | Wholesale Importer UAE & GCC",
    seoDesc: "A comprehensive guide to traditional Japanese tea typologies. Source bulk Sencha, Gyokuro, Hojicha, and Matcha directly from Japan to the Middle East.",
    body: [
      "The vast landscape of Japanese tea is defined less by plant varietals and almost entirely by specialized cultivation, harvesting, and processing techniques. Below is a comprehensive structural guide to the 12 most essential traditional typologies.",
      <div className="my-10 overflow-x-auto rounded-sm border border-border bg-card shadow-sm">
        <table className="w-full text-left text-sm text-foreground/80">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr>
              <th className="px-5 py-4 font-medium whitespace-nowrap">Tea Typology</th>
              <th className="px-5 py-4 font-medium whitespace-nowrap">Processing Style</th>
              <th className="px-5 py-4 font-medium">Key Characteristics</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">1. Sencha</td>
              <td className="px-5 py-4 whitespace-nowrap">Classic Green Tea</td>
              <td className="px-5 py-4">
                The standard domestic green tea. Defined by a crisp balance of refreshing briskness
                and mild structural astringency.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                2. Gyokuro
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Shaded Premium</td>
              <td className="px-5 py-4">
                Cultivated under near-complete shade for ~20 days pre-harvest. Exceptionally high
                umami and profound sweetness with minimal bitterness. Brewed gently at 50°C–60°C.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                3. Kabusecha
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Intermediate Shaded</td>
              <td className="px-5 py-4">
                Shaded for 1 to 10 days prior to harvest. Bridges the sensory gap between Sencha and
                Gyokuro with a pronounced savory umami profile.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">4. Bancha</td>
              <td className="px-5 py-4 whitespace-nowrap">Late-Harvest Common</td>
              <td className="px-5 py-4">
                Harvested from mature leaves later in the agricultural cycle. Exhibits low
                astringency and minimal bitterness for an easy-drinking profile.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                5. Hojicha
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Roasted Green Tea</td>
              <td className="px-5 py-4">
                Crafted by high-roasting Sencha or Bancha foliage. Features a deeply comforting
                roasted nutty aroma and exceptionally low caffeine levels.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                6. Genmaicha
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Brown Rice Blend</td>
              <td className="px-5 py-4">
                A cozy blend of Sencha or Bancha combined with toasted brown rice. Delivers a warm,
                savory, roasted flavor profile ideal for evening relaxation.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">7. Matcha</td>
              <td className="px-5 py-4 whitespace-nowrap">Micro-Milled Powder</td>
              <td className="px-5 py-4">
                Ground entirely from unrolled, shade-grown Tencha leaves. The centerpiece of the
                Japanese tea ceremony, also widely adopted in high-end gastronomy.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                8. Kukicha
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Twig / Stem Tea</td>
              <td className="px-5 py-4">
                Curated from the delicate stems and structural leaf veins sorted during refining.
                Exceptionally sweet, light, and crisp with outstanding cost-efficiency.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                9. Fukamushi Sencha
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Deep-Steamed</td>
              <td className="px-5 py-4">
                Steamed 2 to 3 times longer than standard Sencha. Broken leaf particles brew an
                intense, opaque emerald liquor with a smooth, rich body.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                10. Konacha
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Culinary Flake Tea</td>
              <td className="px-5 py-4">
                The nutrient-dense fine leaf particles collected during Gyokuro and Sencha
                processing. Traditionally served as Agari in sushi bars due to rapid extraction.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">11. Mecha</td>
              <td className="px-5 py-4 whitespace-nowrap">Premium Bud Tea</td>
              <td className="px-5 py-4">
                Composed exclusively of highly concentrated early leaf buds sorted from elite
                Gyokuro and Sencha productions. Intense umami depth favored by connoisseurs.
              </td>
            </tr>
            <tr className="hover:bg-muted/50 transition-colors">
              <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">
                12. Guri Tea
              </td>
              <td className="px-5 py-4 whitespace-nowrap">Coiled Tamaryokucha</td>
              <td className="px-5 py-4">
                Tea leaves intentionally rolled into a curved, comma-like shape rather than straight
                needles. Specialty of Kyushu (Nagasaki, Saga, Kumamoto).
              </td>
            </tr>
          </tbody>
        </table>
      </div>,
    ],
  },
  "green-tea-production": {
    title: "The Step-by-Step Green Tea Production Process",
    date: "January 2026",
    image: "/assets/images/journal/production.png",
    seoTitle: "Japanese Green Tea Production Process | B2B Matcha GCC",
    seoDesc: "Learn how authentic Japanese green tea is produced. We supply premium wholesale matcha, sencha, and hojicha directly from Japan to cafes in Dubai and Saudi Arabia.",
    body: [
      "Crafting premium Japanese green tea involves an unbroken sequence of specialized steps designed to preserve vibrant color, complex aromatics, and structural umami.",
      <div className="my-8 p-6 md:p-10 bg-cream border border-border rounded-sm overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/rice-paper-2.png')] opacity-30 mix-blend-multiply" />
        <div className="relative flex flex-wrap items-center justify-center gap-y-4 gap-x-3">
          {[
            "Cultivation",
            "Plucking",
            "Steaming",
            "Cooling",
            "Coarse Rolling",
            "Rolling",
            "Middle Rolling",
            "Final Rolling",
            "Drying",
            "Sorting",
            "Packaging",
          ].map((step, i, arr) => (
            <div key={step} className="flex items-center gap-3">
              <div className="bg-background border border-border px-3 py-2 text-xs font-medium uppercase tracking-[0.15em] text-foreground shadow-sm">
                {step}
              </div>
              {i < arr.length - 1 && <div className="text-primary/50 text-lg">→</div>}
            </div>
          ))}
        </div>
      </div>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">
        Phase I: Field Cultivation & Harvesting
      </h2>,
      <p>
        <strong>Cultivation & Shading:</strong> Tea fields require meticulous year-round
        agricultural management, including targeted fertilizer application, careful pruning, and
        pest control. In early spring, tender new shoots emerge. For top-tier varieties like Gyokuro
        or Kabusecha, synthetic or traditional covers are placed directly over the plants before
        harvest to block sunlight. This crucial shading process drives up the concentration of
        internal amino acids, which are the primary umami components.
      </p>,
      <p>
        <strong>Plucking:</strong> Between April and May, the premier spring growth is harvested as
        the prized "first flush" (shincha). While machine-picking is utilized for commercial volume,
        ultra-premium grades rely on labor-intensive hand-picking. First flush leaves are
        universally celebrated for their dynamic fragrance and sweet flavor.
      </p>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">
        Phase II: Enzymatic Arrest & Rolling
      </h2>,
      <p>
        <strong>Steaming:</strong> Immediately following plucking, natural oxidizing enzymes begin
        degrading the leaf. To lock in the pristine green color and fresh top-note aromas, the
        leaves are rapidly steamed to deactivate these enzymes. Teas are classified by their precise
        steaming duration:
      </p>,
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>Light Steaming (Asamushi):</strong> Approximately 30 seconds. Yields a light
          golden-green liquor and a crisp, refreshing flavor profile.
        </li>
        <li>
          <strong>Standard Steaming (Chumushi):</strong> 30 to 60 seconds.
        </li>
        <li>
          <strong>Deep Steaming (Fukamushi):</strong> 60 to 120 seconds. The intense heat breaks
          down the leaf structure into finer particles, resulting in a deep, opaque green brew with
          a rich, mellow body.
        </li>
      </ul>,
      <p>
        <strong>Cooling:</strong> Leaving steamed leaves at elevated temperatures causes rapid
        discoloration and flavor flattening. The leaves are blasted with room-temperature air to
        rapidly and uniformly drop their thermal load.
      </p>,
      <p>
        <strong>Coarse Rolling:</strong> Hot, dry air is introduced under controlled mechanical
        pressure. The leaves are gently compressed and rubbed together, generating internal friction
        that softens the cell walls and evaporates surface moisture.
      </p>,
      <p>
        <strong>Rolling:</strong> Performed without external heat, the leaves are gathered into a
        solid mass and pressurized. This equalizes moisture distribution across the batch and
        thoroughly breaks down inner leaf tissues for effortless flavor extraction.
      </p>,
      <p>
        <strong>Middle Rolling:</strong> As moisture escapes, the leaves shrink and twist. Dry hot
        air is reintroduced alongside gentle pressure to loosen and shape the twisted foliage.
      </p>,
      <h2 className="font-serif text-2xl mt-12 text-foreground">
        Phase III: Firing, Refining & Packaging
      </h2>,
      <p>
        <strong>Firing & Final Rolling:</strong> The tea is fired at 80°C to 100°C to stimulate
        internal aromatic compounds. The foliage is then hand-rolled or mechanically rolled in a
        continuous single direction to create the classic slender, needle-like shape of authentic
        green tea.
      </p>,
      <p>
        <strong>Drying:</strong> Hot air reduces internal moisture levels from 10–13% down to a
        shelf-stable 5%. At this milestone, the unrefined tea is designated as <em>Aracha</em>{" "}
        (rough or rough-processed tea). Commercial tea manufacturers typically purchase Aracha
        directly from regional farmers to execute the final finishing stages.
      </p>,
      <p>
        <strong>Sorting & Finishing:</strong> Aracha undergoes mechanical sifting to separate heavy
        stems, fine dust, and broken particles from uniform whole leaves. This yields specialized
        sub-categories like stem tea, powder tea, and leaf tea.
      </p>,
      <p>
        <strong>Blending & Packaging:</strong> Finished leaf, stem, and raw teas are expertly
        blended to harmonize flavor and aroma profiles. This refined master product is called{" "}
        <em>Shiage-cha</em> (finished tea). It is immediately flushed with food-grade nitrogen gas
        and sealed in multi-layer aluminum bags, locking in peak freshness for 6 to 12 months.
      </p>,
    ],
  },
  "what-is-tea": {
    title: "Botanical Foundations: What is Tea?",
    date: "December 2025",
    image: "/assets/images/journal/botanical.png",
    body: [
      "All authentic tea originates from the leaves of a single botanical species: Camellia sinensis, a member of the Theaceae plant family. The vast array of global tea styles is categorized entirely by the degree of enzymatic fermentation (oxidation) the leaves undergo after plucking:",
      "Green Tea (Unfermented Tea): Tea leaves that undergo zero fermentation. The natural oxidation process is deliberately halted immediately after harvest through rapid thermal processing.",
      "Oolong Tea (Semi-fermented Tea): Tea fermented to an intermediate level, occupying the sensory space between green and black tea.",
      "Black Tea (Fully Fermented Tea): Tea where oxidizing enzymes are allowed to fully ferment the leaf structure prior to drying.",
    ],
  },

  "private-label-packaging-basics": {
    title: "Private-label packaging: what to think about first",
    date: "April 2026",
    image: "/assets/images/journal/packaging.png",
    body: [
      "Packaging is usually the longest lead-time item in a private-label launch — it's worth deciding early.",
      "The first question is format: tin, stand-up pouch, sachet, or a hybrid retail-and-foodservice setup. Each has implications for shelf life, perceived value, and freight cost.",
      "From there, label specs, finish, and any embossing or foiling drive minimum order quantities and timeline. Plan on 4–8 weeks from approved artwork to delivery for most blank-label production.",
    ],
  },
  "hojicha-the-quiet-staple": {
    title: "Hojicha — the quiet staple",
    date: "March 2026",
    image: "/assets/images/journal/hojicha.png",
    body: [
      "Hojicha is roasted green tea. It's low in caffeine, warm and toasty, and increasingly the tea cafés reach for when they want something distinct from matcha.",
      "On a wholesale menu it earns its place twice: as a leaf for steeping and as a fine powder for lattes, soft serve and pastry. Both forms hold up to milk far better than green tea does.",
      "If you're building a tea program from scratch, hojicha is a quiet bet that almost always pays off — easy to drink, easy to merchandise, and unmistakably Japanese.",
    ],
  },
};

export const Route = createFileRoute("/journal/$slug")({
  loader: ({ params }) => {
    const post = POSTS[params.slug];
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seoTitle || `${loaderData.title} – Wholesale QT Foods`,
          description:
            loaderData.seoDesc ||
            (typeof loaderData.body[0] === "string"
              ? loaderData.body[0].slice(0, 160)
              : loaderData.title),
          image: loaderData.image ?? undefined,
        })
      : [],
  }),
  component: Page,
  notFoundComponent: () => (
    <SiteLayout breadcrumbs={[{ label: "Journal", href: "/journal" }, { label: "Not Found" }]}>
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="font-serif text-4xl">Post not found</h1>
        <Link to="/journal" className="mt-6 inline-block text-primary hover:underline">
          ← Back to Journal
        </Link>
      </div>
    </SiteLayout>
  ),
});

function Page() {
  const post = Route.useLoaderData();
  return (
    <SiteLayout breadcrumbs={[{ label: "Journal", href: "/journal" }, { label: post.title }]}>
      <article className="mx-auto max-w-4xl px-5 pt-10 pb-20 lg:px-8 lg:pt-14 lg:pb-28">
        <p className="mt-6 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          {post.date}
        </p>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">{post.title}</h1>
        {post.image && (
          <div className="mt-8 overflow-hidden rounded-sm">
            <img src={post.image} alt={post.title} className="aspect-[9/4] w-full object-cover" />
          </div>
        )}
        <div className="mt-10 space-y-5 text-base leading-relaxed text-foreground/85">
          {post.body.map((node: ReactNode, i: number) =>
            typeof node === "string" ? <p key={i}>{node}</p> : <div key={i}>{node}</div>,
          )}
        </div>
      </article>
    </SiteLayout>
  );
}
